import {
  Button,
  Grid,
  CssBaseline,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Dialog as MuiDialog,
  Switch
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import IconSaveOrUpdate from '@mui/icons-material/SaveOutlined';
import CustomBreadCrumb from '../../common/CustomBreadCrumb';
import { GlobalEdiApiConstants } from '../../../Constants/GlobalEdiApiConstants';
import { CommonService } from '../../../services';
import { Alert } from "@mui/lab";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ClearIcon from '@mui/icons-material/Clear';
import IconRestore from '@mui/icons-material/AutorenewOutlined';

const ProcessLevelListDialog = (props) => {
  const theme = useTheme();
  const { 
    onClose, 
    open, 
    title, 
    mode, 
    dialogHeader, 
    orgName, 
    entityName, 
    ensObjectToBeEditedOrView
  } = props;
  
  const [componentList, setComponentList] = useState(title);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [allProcesses, setAllProcesses] = useState([]);
  const [savedProcessLevels, setSavedProcessLevels] = useState([]);
  const [toggleStates, setToggleStates] = useState({});
  const [originalToggleStates, setOriginalToggleStates] = useState({});
  const [lastSavedToggleStates, setLastSavedToggleStates] = useState({});
  
  const [alertBoxState, setAlertBoxState] = useState({
    openAlert: false,
    vertical: 'top',
    horizontal: 'center',
    message: '',
    severity: ''
  });

  const apiUrlAndParamBodyConstants = GlobalEdiApiConstants();
  const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const enabledCount = Object.values(toggleStates).filter(Boolean).length;

  const { vertical, horizontal, openAlert, message, severity } = alertBoxState;

  const getProcessId = (process) => {
    if (!process) return null;
    let processId = process.id || process.processId || process.value?.id || process.value?.processId || process.value;
    if (typeof processId === 'string') processId = parseInt(processId, 10);
    return processId;
  };

  const getProcessName = (process) => {
    if (!process) return 'Unknown Process';
    return process.name || process.process || process.label || process.value?.name || 'Unknown Process';
  };

  const fetchAllProcesses = () => {
    setIsLoadingProcesses(true);
    const bpType = ensObjectToBeEditedOrView?.bpType || '';
    
    const requestBody = {
      body: {
        process: "",
        organization: { erpOrgCode: "", orgName: "" },
        processType: "MSG",
        bpType: bpType || "SUPPLIER",
        active: true
      },
      pageNo: 0,
      sortDir: "asc",
      sortKey: "process",
      recordsPerPage: 100
    };
    
    CommonService.GetListApi(`/processMaster`, requestBody)
      .then((response) => {
        let processes = [];
        if (response.data && Array.isArray(response.data)) {
          processes = response.data;
        } else if (response.data?.content && Array.isArray(response.data.content)) {
          processes = response.data.content;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          processes = response.data.data;
        }
        setAllProcesses(processes);
        setIsLoadingProcesses(false);
      })
      .catch((error) => {
        console.error('Error fetching processes:', error);
        setAllProcesses([]);
        setIsLoadingProcesses(false);
        showAlert("Failed to fetch processes.", "error");
      });
  };

  const fetchProcessLevels = (emailNotificationId) => {
    setIsLoading(true);
    
    CommonService.GetListApi(`/emailNotificationProcessLevel/${emailNotificationId}`)
      .then((response) => {
        let levels = [];
        if (response.data && Array.isArray(response.data)) {
          levels = response.data;
        } else if (response.data?.content && Array.isArray(response.data.content)) {
          levels = response.data.content;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          levels = response.data.data;
        }
        
        const processedLevels = levels.map(level => ({
          ...level,
          id: level.id,
          processId: level.processMaster?.processId || level.processId,
          processName: level.processMaster?.name || level.processName,
        }));
        
        setSavedProcessLevels(processedLevels);
        
        const initialStates = {};
        processedLevels.forEach(level => {
          const pId = level.processId;
          if (pId) initialStates[pId] = true;
        });
        
        setToggleStates(initialStates);
        setOriginalToggleStates(initialStates);
        setLastSavedToggleStates(initialStates);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching process levels:', error);
        setSavedProcessLevels([]);
        setToggleStates({});
        setOriginalToggleStates({});
        setLastSavedToggleStates({});
        setIsLoading(false);
        showAlert("Failed to fetch process levels.", "error");
      });
  };

  const handleToggleChange = (processId) => {
    setToggleStates(prev => ({
      ...prev,
      [processId]: !prev[processId]
    }));
  };

  const hasChanges = () => {
    return allProcesses.some(p => {
      const pId = getProcessId(p);
      return !!toggleStates[pId] !== !!originalToggleStates[pId];
    });
  };

  const hasChangesFromLastSaved = () => {
    return allProcesses.some(p => {
      const pId = getProcessId(p);
      return !!toggleStates[pId] !== !!lastSavedToggleStates[pId];
    });
  };

  const showAlert = (message, severity) => {
    setAlertBoxState({ 
      openAlert: true, 
      vertical: 'top', 
      horizontal: 'center', 
      message, 
      severity 
    });
  };

  const handleReset = () => {
    setToggleStates({...lastSavedToggleStates});
    showAlert("Changes reset", "info");
  };

  const handleSaveAll = async () => {
    const emailNotificationId = ensObjectToBeEditedOrView?.id || ensObjectToBeEditedOrView?.emailNotificationId;
    
    if (!emailNotificationId || emailNotificationId <= 0) {
      showAlert("Invalid ID", "error");
      return;
    }
    
    const currentUser = CommonService.getLoggedInUserInfo();
    const currentUserName = currentUser?.userName || 'SYSTEM';
    const currentTime = new Date().toISOString();
    
    setIsFormSubmitted(true);
    
    const toAdd = [];
    const toDelete = [];
    
    allProcesses.forEach(process => {
      const pId = getProcessId(process);
      const isCurrentlyEnabled = !!toggleStates[pId];
      const wasOriginallyEnabled = !!originalToggleStates[pId];
      
      if (isCurrentlyEnabled && !wasOriginallyEnabled) {
        toAdd.push(process);
      } else if (!isCurrentlyEnabled && wasOriginallyEnabled) {
        const savedLevel = savedProcessLevels.find(l => l.processId === pId);
        if (savedLevel?.id) {
          toDelete.push(savedLevel);
        }
      }
    });
    
    try {
      for (const level of toDelete) {
        await CommonService.deleteByIdApi(`/emailNotificationProcessLevel/${level.id}`, {});
      }
      
      for (const process of toAdd) {
        const processId = getProcessId(process);
        const processName = getProcessName(process);
        const isEnabled = !!toggleStates[processId];
        
        const requestData = {
          emailNotificationId: parseInt(emailNotificationId),
          processMaster: { 
            processId: parseInt(processId),
            processName: processName
          },
          active: isEnabled,
          createdBy: currentUserName,
          updatedBy: currentUserName,
          createdTime: currentTime,
          updatedTime: currentTime,
          processId: parseInt(processId),
          processName: processName
        };
        
        await CommonService.saveOrUpdateOrPost(
          apiUrlAndParamBodyConstants.commonApiUrlForAllTheScreen.ProcessLevelListDialog || '/emailNotificationProcessLevel', 
          requestData
        );
      }
      
      setLastSavedToggleStates({...toggleStates});
      setOriginalToggleStates({...toggleStates});
      
      fetchProcessLevels(emailNotificationId);
      setIsFormSubmitted(false);
      showAlert("Saved successfully", "success");
      
    } catch (error) {
      setIsFormSubmitted(false);
      const errorMessage = error.response?.data?.message || error.message || "Error saving";
      showAlert(errorMessage, "error");
    }
  };

  const handleCloseDialog = () => {
    onClose(false);
  };

  const handleCloseAlert = () => {
    setAlertBoxState(prev => ({ ...prev, openAlert: false }));
  };

  useEffect(() => {
    if (mode !== '') {
      let currentRouterComponent;
      if (mode === 'create' || mode === 'edit') {
        currentRouterComponent = [{
          path: null,
          name: <FormattedMessage id="process.level.dialog.title" defaultMessage="Process Level Configuration" />,
          iconName: FormatListBulletedIcon,
        }];
      }
      setComponentList(title.concat(currentRouterComponent || []));
    }
  }, [mode]);

  useEffect(() => {
    if (open && ensObjectToBeEditedOrView) {
      const emailNotificationId = ensObjectToBeEditedOrView?.id || ensObjectToBeEditedOrView?.emailNotificationId;
      fetchAllProcesses();
      
      if (emailNotificationId && emailNotificationId > 0) {
        fetchProcessLevels(emailNotificationId);
      } else {
        setSavedProcessLevels([]);
        setToggleStates({});
        setOriginalToggleStates({});
        setLastSavedToggleStates({});
      }
    }
  }, [open, ensObjectToBeEditedOrView]);

  const renderProcessItem = (process) => {
    const processId = getProcessId(process);
    const processName = getProcessName(process);
    const isEnabled = !!toggleStates[processId];
    
    return (
      <Grid item xs={12} sm={6} md={4} key={processId}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%',
            minHeight: 56,
            bgcolor: isEnabled ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            borderColor: isEnabled ? 'primary.main' : 'divider',
            borderWidth: isEnabled ? 2 : 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: isEnabled ? 'rgba(25, 118, 210, 0.12)' : 'action.hover',
              borderColor: isEnabled ? 'primary.main' : 'grey.400'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: isEnabled ? 600 : 400,
                color: isEnabled ? 'primary.dark' : 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {processName}
            </Typography>
          </Box>
          <Switch
            checked={isEnabled}
            onChange={() => handleToggleChange(processId)}
            color="primary"
            size="small"
          />
        </Paper>
      </Grid>
    );
  };

  const renderContent = () => {
    if (isLoading || isLoadingProcesses) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            <FormattedMessage id="loading.processes" defaultMessage="Loading processes..." />
          </Typography>
        </Box>
      );
    }

    if (allProcesses.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="no.processes.available" defaultMessage="No processes available" />
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxHeight: 320, overflow: 'auto', pr: 0.5 }}>
        <Grid container spacing={1.5}>
          {allProcesses.map(process => renderProcessItem(process))}
        </Grid>
      </Box>
    );
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={openAlert}
        onClose={handleCloseAlert}
        autoHideDuration={4000}
      >
        <Alert severity={severity}>{message}</Alert>
      </Snackbar>
      
      <MuiDialog 
        fullWidth 
        maxWidth='md' 
        onClose={handleCloseDialog}
        open={open} 
        disableBackdropClick={true}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Header with Process Levels inline */}
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          p: 2, 
          borderTopLeftRadius: 8, 
          borderTopRightRadius: 8 
        }}>
          <Box sx={{  
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center', 
            width: '100%' 
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                <CustomBreadCrumb componentList={componentList} isDialog={true} />
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {orgName} {dialogHeader} {entityName}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center', 
              gap: 1,
              flexShrink: 0,
              ml: 2 
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                <FormattedMessage id="process.levels.list" defaultMessage="Process Levels" />
              </Typography>
              <Avatar sx={{
                width: 26,
                height: 26,
                bgcolor: 'white',
                color: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {enabledCount}
              </Avatar>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2, flex: 1, overflow: 'hidden' }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2, height: '100%' }}>
              {renderContent()}
            </CardContent>
          </Card>
        </Box>

        {/* Footer Actions with Custom Colors */}
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          bgcolor: 'grey.50'  // Light gray background for footer
        }}>
          {/* Cancel Button with Primary Background and Icon */}
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            endIcon={<ClearIcon />}
            sx={{ 
              minWidth: 100,
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <FormattedMessage id="common.button.cancel.lable" defaultMessage="Cancel" />
          </Button>

          {/* Reset Button with Black Background and Icon */}
          <Button
            variant="contained"
            disabled={isFormSubmitted || !hasChangesFromLastSaved()}
            onClick={handleReset}
            endIcon={<IconRestore />}
            sx={{ 
              minWidth: 100,
              bgcolor: '#000000',  // Black background
              color: 'white',
              '&:hover': {
                bgcolor: '#333333'  // Darker black on hover
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.400',
                color: 'grey.600'
              }
            }}
          >
            <FormattedMessage id="common.button.reset.lable" defaultMessage="Reset" />
          </Button>

          {/* Save Button (Kept as default primary) */}
          <Button
            variant="contained"
            color="primary"
            disabled={isFormSubmitted || !hasChanges()}
            onClick={handleSaveAll}
            endIcon={isFormSubmitted ? 
              <CircularProgress size={20} color="inherit" /> : 
              <IconSaveOrUpdate />
            }
            sx={{ minWidth: 100 }}
          >
            {isFormSubmitted ? (
              <FormattedMessage id="common.button.save" defaultMessage="Saving" />
            ) : (
              <FormattedMessage id="common.button.save" defaultMessage="Save" />
            )}
          </Button>
        </Box>
      </MuiDialog>
    </React.Fragment>
  );
};

export default ProcessLevelListDialog;